import React, { useState, useEffect, useRef } from 'react';
import StorageManager from './services/StorageManager';
import { AnimatedFlameIcon } from './components/Icons';

// Original public pages
import LandingPageWall from './pages/LandingPageWall';
import LandingPage from './pages/LandingPage';
import BrowsePage from './pages/BrowsePage';
import GenreLandingPage from './pages/GenreLandingPage';
import GenreSelectionPage from './pages/GenreSelectionPage';
import CreatorsLandingPage from './pages/CreatorsLandingPage';
import CreatorApplyPage from './pages/CreatorApplyPage';
import CreatorApplicationPendingPage from './pages/CreatorApplicationPendingPage';
import CreatorPortal from './pages/CreatorPortal';
import CreatorUploadNew from './pages/CreatorUploadNew';
import UploadSuccessPage from './pages/UploadSuccessPage';
import AccountPage from './pages/AccountPage';
import AboutPage from './pages/AboutPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PilotAnalytics from './pages/PilotAnalytics';

function PilotLightPlatform() {
  const [currentView, setCurrentView] = useState('landing');
  const [pilots, setPilots] = useState([]);
  const [selectedPilot, setSelectedPilot] = useState(null);
  const [selectedGenre, setSelectedGenre] = useState(null);
  // DEV BYPASS: auto-login to skip the landing wall during local development
  const [currentUser, setCurrentUser] = useState(
    window.location.hostname === 'localhost'
      ? { id: 'dev-user', email: 'dev@pilotlight.test', displayName: 'Dev User', role: 'voter' }
      : null
  );
  const [loading, setLoading] = useState(true);
  const [resetToken, setResetToken] = useState(null);
  const [isCreatorSession, setIsCreatorSession] = useState(false);
  const [industryContact, setIndustryContact] = useState(null);
  const [showNda, setShowNda] = useState(false);
  const [ndaLoading, setNdaLoading] = useState(false);

  // === URL TOKEN DETECTION (reset-password OR magic link verify) ===
  const resetPasswordDetectedRef = useRef(false);
  const magicLinkDetectedRef = useRef(false);

 useEffect(() => {
    const hash = window.location.hash;
    if (hash.startsWith('#reset-password')) {
      const queryPart = hash.split('?')[1];
      if (queryPart) {
        const params = new URLSearchParams(queryPart);
        const token = params.get('token');
        if (token) {
          resetPasswordDetectedRef.current = true;
          setResetToken(token);
          setCurrentView('reset-password');
        }
      }
    } else if (hash.startsWith('#verify')) {
      const queryPart = hash.split('?')[1];
      if (queryPart) {
        const params = new URLSearchParams(queryPart);
        const token = params.get('token');
        if (token) {
          magicLinkDetectedRef.current = true;
          verifyMagicLink(token);
        }
      }
    }
  }, []);

  const verifyMagicLink = async (token) => {
    try {
      const resp = await fetch(`/.netlify/functions/api-industry-verify?token=${encodeURIComponent(token)}`);
      const data = await resp.json();
      if (data.valid) {
        const contact = data.contact;
        setIndustryContact(contact);
        const industryUser = {
          id: `industry_${contact.email}`,
          email: contact.email,
          displayName: contact.name || contact.email,
          name: contact.name || '',
          company: contact.company || '',
          role: 'industry'
        };
        setCurrentUser(industryUser);
        sessionStorage.setItem('industryToken', token);
        if (!contact.ndaSigned) {
          setShowNda(true);
          setCurrentView('industry-nda');
        } else {
          setCurrentView('browse');
        }
      } else {
        setCurrentView('magic-link-expired');
      }
    } catch (err) {
      console.error('Magic link verification failed:', err);
      setCurrentView('magic-link-expired');
    }
    setLoading(false);
  };

  const handleSignNda = async () => {
    if (!industryContact) return;
    setNdaLoading(true);
    try {
      await fetch('/.netlify/functions/api-industry-sign-agreement', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: industryContact.email })
      });
      setShowNda(false);
      setCurrentView('browse');
    } catch (err) {
      console.error('NDA sign error:', err);
    }
    setNdaLoading(false);
  };

  // Track if we're handling a popstate event (to avoid pushing history)
  const isPopstateRef = useRef(false);
  const previousViewRef = useRef(currentView);

  // Push to history whenever currentView changes (except from popstate)
  useEffect(() => {
    if (loading) return;
    if (isPopstateRef.current) {
      isPopstateRef.current = false;
      previousViewRef.current = currentView;
      return;
    }
    if (previousViewRef.current !== currentView) {
      const state = { view: currentView, pilotId: selectedPilot?.id, genre: selectedGenre };
      window.history.pushState(state, '', `#${currentView}`);
      previousViewRef.current = currentView;
    }
  }, [currentView, loading]);

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = (event) => {
      isPopstateRef.current = true;
      if (event.state && event.state.view) {
        setCurrentView(event.state.view);
        if (event.state.genre) setSelectedGenre(event.state.genre);
      } else {
        setCurrentView('landing');
      }
    };
    window.addEventListener('popstate', handlePopState);

    if (!window.history.state && !resetPasswordDetectedRef.current) {
      window.history.replaceState({ view: 'landing' }, '', '#landing');
    }

    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Save navigation state to localStorage whenever it changes
  useEffect(() => {
    if (!loading && currentView !== 'landing') {
      const navState = {
        view: currentView,
        pilotId: selectedPilot?.id || null,
        genre: selectedGenre || null
      };
      localStorage.setItem('pilotlight_nav', JSON.stringify(navState));
    }
  }, [currentView, selectedPilot, selectedGenre, loading]);

  useEffect(() => {
    const init = async () => {
      try {
        // === DEV MODE: Skip login when Airtable API is unavailable ===
        // Remove this block when API is back (April 1st)
        const DEV_BYPASS_LOGIN = import.meta.env.DEV;
        if (DEV_BYPASS_LOGIN) {
          const mockUser = {
            id: 'dev-user',
            name: 'Dev Tester',
            email: 'dev@pilotlighthq.com',
            creatorStatus: 'approved',
            displayName: 'Dev Tester'
          };
          setCurrentUser(mockUser);
          setPilots([]);
          setCurrentView('browse');
          setLoading(false);
          return;
        }
        // === END DEV MODE ===

        // If reset-password or magic link was detected from URL, skip normal init routing
        if (resetPasswordDetectedRef.current || magicLinkDetectedRef.current) {
          if (resetPasswordDetectedRef.current) setLoading(false);
          return;
        }

        // Check for voter session (unified login)
        const cachedVoter = StorageManager.getCurrentVoter();
        const hasSessionToken = !!localStorage.getItem('pilotLightSessionToken');

        // If cached voter but no session token, clear stale pre-migration session
        if (cachedVoter && !hasSessionToken) {
          StorageManager.logoutVoter();
          setCurrentView('landing');
          setLoading(false);
          return;
        }

        if (cachedVoter) {
          // Refresh voter data
          let voter = cachedVoter;
          try {
            const allVoters = await StorageManager.getVoters();
            const freshVoter = allVoters.find(v => v.id === cachedVoter.id);
            if (freshVoter) {
              voter = { ...cachedVoter, ...freshVoter };
              StorageManager.setCurrentVoter(voter);
            }
          } catch (e) {
            console.warn('Could not refresh voter data:', e.message);
          }

          setCurrentUser(voter);
          const myPilots = await StorageManager.getMyPilots(voter.id);
          setPilots(myPilots || []);

          // Restore navigation state from localStorage
          const savedNav = localStorage.getItem('pilotlight_nav');
          if (savedNav) {
            try {
              const navState = JSON.parse(savedNav);
              const validViews = ['browse', 'genre-landing', 'genre-selection', 'creators-landing',
                'creator-apply', 'creator-application-pending', 'creator-portal', 'creator-upload',
                'upload-success', 'account', 'about', 'privacy', 'terms', 'pilot-analytics'];
              if (navState.genre) setSelectedGenre(navState.genre);
              if (validViews.includes(navState.view)) {
                setCurrentView(navState.view);
              } else {
                setCurrentView('browse');
              }
            } catch (e) {
              console.error('Error restoring nav state:', e);
              setCurrentView('browse');
            }
          } else {
            setCurrentView('browse');
          }
        } else {
          // No user logged in - go to landing
          setCurrentView('landing');
        }
      } catch (err) {
        console.error('Error initializing app:', err);
      }
      setLoading(false);
    };
    init();
  }, []);

  const refreshPilots = async () => {
    if (currentUser) {
      try {
        const myPilots = await StorageManager.getMyPilots(currentUser.id);
        setPilots(myPilots || []);
      } catch (err) {
        console.error('Error refreshing pilots:', err);
      }
    }
  };

  // Auto-scroll to top when navigating to a new page
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentView]);

  const handleLogin = async (user, isCreatorLogin = false) => {
    // Grant automatic creator access to admin account
    if (user.email === 'admin@pilotlighthq.com' && user.creatorStatus !== 'approved') {
      user.creatorStatus = 'approved';
      try {
        await StorageManager.approveCreatorApplication(user.id);
      } catch (e) {
        console.log('Admin auto-approval (local only):', e);
      }
    }

    // Route based on login type: creators go to portal, industry members go to viewing room
    setCurrentUser(user);
    if (isCreatorLogin && user.creatorStatus === 'approved') {
      setIsCreatorSession(true);
      setCurrentView('creator-portal');
    } else {
      setIsCreatorSession(false);
      setCurrentView('browse');
    }
    StorageManager.setCurrentVoter(user);

    // Load pilots async
    try {
      const myPilots = await StorageManager.getMyPilots(user.id);
      setPilots(myPilots || []);
    } catch (err) {
      console.error('Error loading pilots after login:', err);
      setPilots([]);
    }
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to log out?')) {
      StorageManager.logoutVoter();
      localStorage.removeItem('pilotlight_nav');
      setCurrentUser(null);
      setPilots([]);
      setSelectedPilot(null);
      setSelectedGenre(null);
      setIsCreatorSession(false);
      setCurrentView('landing');
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#000000',
        display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
        <div style={{ textAlign: 'center' }}>
          <AnimatedFlameIcon size={48} style={{ margin: '0 auto 1rem', display: 'block' }} />
        </div>
      </div>
    );
  }

  const renderView = () => {
    // Auth guard: these routes require a logged-in user
    const authRequired = ['browse', 'genre-landing', 'genre-selection', 'creator-portal',
      'creator-upload', 'upload-success', 'account', 'pilot-analytics'].includes(currentView);
    if (authRequired && !currentUser) {
      return <LandingPage onLogin={(user) => handleLogin(user, false)} />;
    }

    // Creator guard: if logged in via creator toggle, keep them in the portal
    if (isCreatorSession && ['browse', 'genre-landing', 'genre-selection', 'account'].includes(currentView)) {
      setCurrentView('creator-portal');
      return null;
    }

    switch(currentView) {
      case 'landing':
        return <LandingPage onLogin={(user) => handleLogin(user, false)} />;

      case 'industry-nda':
        return (
          <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#000', color: '#fff', fontFamily: "'Cormorant Garamond', Georgia, serif", padding: '2rem' }}>
            <div style={{ maxWidth: '560px', width: '100%', textAlign: 'center' }}>
              <h1 style={{ fontSize: '1.6rem', fontWeight: 300, letterSpacing: '0.15em', marginBottom: '0.5rem', textTransform: 'uppercase', background: 'linear-gradient(135deg, #ff6b6b 0%, #feca57 50%, #4ecdc4 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Pilot Light</h1>
              <p style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.5)', marginBottom: '2rem' }}>Industry Screening Room</p>

              <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', padding: '2rem', textAlign: 'left', marginBottom: '1.5rem' }}>
                <h2 style={{ fontSize: '1.15rem', fontWeight: 400, marginBottom: '1.2rem', color: 'rgba(255,255,255,0.9)' }}>Non-Disclosure Agreement</h2>
                <div style={{ fontSize: '0.9rem', lineHeight: 1.8, color: 'rgba(255,255,255,0.6)' }}>
                  <p>By accessing the Pilot Light Screening Room, you agree to the following:</p>
                  <ul style={{ paddingLeft: '1.2rem', margin: '1rem 0' }}>
                    <li style={{ marginBottom: '0.6rem' }}>All content viewed in the Screening Room is confidential and proprietary to the respective creators.</li>
                    <li style={{ marginBottom: '0.6rem' }}>You will not record, screenshot, distribute, or share any content from the Screening Room.</li>
                    <li style={{ marginBottom: '0.6rem' }}>You will not disclose the identity of creators, plot details, or any other information about the pilots to anyone outside of Pilot Light.</li>
                    <li style={{ marginBottom: '0.6rem' }}>This access is granted solely for professional evaluation purposes.</li>
                  </ul>
                </div>
              </div>

              <button onClick={handleSignNda} disabled={ndaLoading}
                style={{ padding: '0.85rem 3rem', background: 'none', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '10px', color: 'rgba(255,255,255,0.7)', fontSize: '1rem', fontFamily: "'Cormorant Garamond', Georgia, serif", cursor: ndaLoading ? 'wait' : 'pointer', letterSpacing: '0.08em', transition: 'all 0.25s ease' }}
                onMouseOver={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)'; e.currentTarget.style.color = 'rgba(255,255,255,0.9)'; }}
                onMouseOut={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; }}
              >{ndaLoading ? 'Processing...' : 'I Agree — Enter Screening Room'}</button>
            </div>
          </div>
        );

      case 'magic-link-expired':
        return (
          <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#000', color: '#fff', fontFamily: "'Cormorant Garamond', Georgia, serif", padding: '2rem' }}>
            <div style={{ maxWidth: '400px', textAlign: 'center' }}>
              <h1 style={{ fontSize: '1.6rem', fontWeight: 300, letterSpacing: '0.15em', marginBottom: '0.5rem', textTransform: 'uppercase', background: 'linear-gradient(135deg, #ff6b6b 0%, #feca57 50%, #4ecdc4 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Pilot Light</h1>
              <p style={{ fontSize: '1.1rem', color: 'rgba(255,255,255,0.6)', marginBottom: '1.5rem' }}>This access link has expired or is invalid.</p>
              <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.35)', lineHeight: 1.7 }}>
                Contact <a href="mailto:admin@pilotlighthq.com" style={{ color: 'rgba(255,255,255,0.5)', textDecoration: 'underline' }}>admin@pilotlighthq.com</a> to request a new link.
              </p>
            </div>
          </div>
        );

      case 'forgot-password':
        return <ForgotPasswordPage
          onBack={() => setCurrentView('landing')}
          onResetSent={() => setCurrentView('reset-password')} />;

      case 'reset-password':
        return <ResetPasswordPage
          token={resetToken}
          onSuccess={() => { setResetToken(null); setCurrentView('landing'); }}
          onBack={() => { setResetToken(null); setCurrentView('landing'); }} />;

      case 'browse':
        return <BrowsePage
          currentUser={currentUser}
          onLogout={handleLogout}
          onSelectPilot={(pilot) => { setSelectedPilot(pilot); }}
          onLogin={(user) => handleLogin(user)}
          onNavigate={(view) => setCurrentView(view)} />;

      case 'genre-landing':
        return <GenreLandingPage
          genre={selectedGenre}
          currentUser={currentUser}
          onLogout={handleLogout}
          onSelectPilot={(pilot) => { setSelectedPilot(pilot); }}
          onBack={() => setCurrentView('browse')}
          onPitchPilot={() => setCurrentView('creators-landing')}
          onLogin={(user) => handleLogin(user)}
          onNavigate={(view) => setCurrentView(view)}
          onSwitchGenre={(genre) => { setSelectedGenre(genre); }} />;

      case 'genre-selection':
        return <GenreSelectionPage
          currentUser={currentUser}
          onLogout={handleLogout}
          onSelectGenre={(genre) => { setSelectedGenre(genre); setCurrentView('genre-landing'); }}
          onPitchPilot={() => setCurrentView('creators-landing')}
          onLogin={(user) => handleLogin(user)}
          onNavigate={(view) => setCurrentView(view)} />;

      case 'creators-landing':
        return <CreatorsLandingPage
          currentUser={currentUser}
          onBack={() => setCurrentView('browse')}
          onNavigate={(view) => setCurrentView(view)}
          onApply={() => setCurrentView('creator-apply')}
          onLogin={(user) => handleLogin(user)} />;

      case 'creator-apply':
        if (!currentUser) { setCurrentView('landing'); return null; }
        return <CreatorApplyPage
          currentUser={currentUser}
          onBack={() => setCurrentView('creators-landing')}
          onSubmit={() => setCurrentView('creator-application-pending')}
          onNavigate={(view) => setCurrentView(view)} />;

      case 'creator-application-pending':
        return <CreatorApplicationPendingPage
          onBrowse={() => setCurrentView('browse')}
          onNavigate={(view) => setCurrentView(view)} />;

      case 'creator-portal':
        if (!currentUser) { setCurrentView('landing'); return null; }
        if (currentUser.creatorStatus !== 'approved') {
          setCurrentView('browse');
          return null;
        }
        return <CreatorPortal
          currentUser={currentUser}
          pilots={pilots}
          onHome={() => setCurrentView('browse')}
          onUploadNew={() => setCurrentView('creator-upload')}
          onSelectPilot={(pilot) => { setSelectedPilot(pilot); }}
          onRefreshPilots={refreshPilots}
          onUserUpdate={(updatedUser) => setCurrentUser(updatedUser)}
          onLogout={handleLogout} />;

      case 'creator-upload':
        if (!currentUser) { setCurrentView('landing'); return null; }
        if (currentUser.creatorStatus !== 'approved') {
          setCurrentView('browse');
          return null;
        }
        return <CreatorUploadNew
          currentUser={currentUser}
          onHome={() => setCurrentView('browse')}
          onBack={() => setCurrentView('creator-portal')}
          onSubmit={async () => { await refreshPilots(); setCurrentView('upload-success'); }} />;

      case 'upload-success':
        return <UploadSuccessPage
          onHome={() => setCurrentView('browse')}
          onCreatorPortal={() => setCurrentView('creator-portal')} />;

      case 'account':
        if (!currentUser) { setCurrentView('landing'); return null; }
        return <AccountPage
          currentUser={currentUser}
          onBack={() => setCurrentView('browse')}
          onNavigate={(view) => setCurrentView(view)}
          onLogout={handleLogout} />;

      case 'about':
        return <AboutPage
          currentUser={currentUser}
          onBack={() => setCurrentView('browse')}
          onNavigate={(view) => setCurrentView(view)} />;

      case 'privacy':
        return <PrivacyPage
          currentUser={currentUser}
          onBack={() => setCurrentView('browse')}
          onNavigate={(view) => setCurrentView(view)} />;

      case 'terms':
        return <TermsPage
          currentUser={currentUser}
          onBack={() => setCurrentView('browse')}
          onNavigate={(view) => setCurrentView(view)} />;

      case 'pilot-analytics':
        return <PilotAnalytics
          pilot={selectedPilot}
          onBack={() => setCurrentView('creator-portal')}
          onRefresh={refreshPilots} />;

      default:
        if (currentUser) {
          return <BrowsePage
            currentUser={currentUser}
            onLogout={handleLogout}
            onSelectPilot={(pilot) => { setSelectedPilot(pilot); }}
            onLogin={(user) => handleLogin(user)}
            onNavigate={(view) => setCurrentView(view)} />;
        } else {
          return <LandingPage onLogin={(user) => handleLogin(user, false)} />;
        }
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#000000',
      fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#fff' }}>
      {renderView()}
    </div>
  );
}

export default PilotLightPlatform;
